import {
  ApiCompanyJobDetail,
  ApiJobDetailOk, ApiJobUserDetailOk, ApiPrevNextJobOk,
} from '@type/v1-api-types';
import {
  Button, Card, LinkStyled, SanitizeHTML,
} from '@components/common';
import { detailSectionsStyles } from '@components/templates/job/detail/detailSectionStyles';
import { useTranslation } from 'next-i18next';
import { clsx } from 'clsx';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { ModalPrevNextJobs } from '@type/job';
import { AiEvaluateProfile } from '@components/templates/job/detail/sections/AiEvaluateProfile';
import { LanguageIcon, ReceiptRefundIcon } from '@heroicons/react/16/solid';
import { ArchiveBoxIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { JobType } from '@type/job-params';
import { StatusBar } from '@components/templates/my-jobs/Card/partials/StatusBar';
import { trackEvent } from '@services/tracking';
import { useSearchParams } from 'next/navigation';
import { useTwoColumnLayout } from '@components/layout/Layouts/TwoColumnLayout/TwoColumnLayoutContext';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
// eslint-disable-next-line import/no-cycle
import {
  CompanyShortPlaceholder,
  CompanyWidgetPlaceholder,
  DescriptionPlaceholder,
  TagsPlaceholder,
  TitlePlaceholder,
  SummaryPlaceholder,
} from './placeholders';
// eslint-disable-next-line import/no-cycle
import {
  CompanyWidget,
  JobActions,
  Navigation,
  ShareAndNavigation,
  SimilarJobs,
  Tags,
  CompanyShort,
  Summary,
  OnlineInterview,
} from './sections';
import { ApplicantsTags } from './sections/company/ApplicantsTags';
import { CandidatesTags } from './sections/company/CandidatesTags';

// Interface
// ************************************

// Common properties shared by all modes
interface CommonJobDetailProps {
  locale: Locale;
  modalPrevNextJobs?: ModalPrevNextJobs;
  closeModal?: () => void;
  isLoading?: boolean;
  isSinglePage?: boolean;
  isSkeletonPlaceholder?: boolean;
  onTriggerRefund?: () => void;
  jobTitle?: string;
  modalNavigateToJob?: (nextJobSlug: string, direction: 'prev' | 'next') => void;
  hideNavigation?: boolean;
}

// Props for 'default' and 'preview' modes
interface JobDetailDefaultProps extends CommonJobDetailProps {
  mode: 'default' | 'preview';
  job: (ApiJobDetailOk & ApiJobUserDetailOk) | null;
  similarJobs?: ApiSchema<'JobResult'> | null;
  apiPrevNextJobs?: ApiPrevNextJobOk | null;
  mutateUserDetail?: () => void;
  revalidateJobData?: undefined;
}

// Props for 'company' mode
interface JobDetailCompanyProps extends CommonJobDetailProps {
  mode: 'company';
  job: ApiCompanyJobDetail | null;
  similarJobs?: undefined;
  apiPrevNextJobs?: undefined;
  mutateUserDetail?: undefined;
  revalidateJobData?: (jobSlug: string, oldStatus: string, newStatus: string) => void;
}

// XOR type for JobDetailProps based on 'mode'
export type JobDetailProps =
  | JobDetailDefaultProps
  | JobDetailCompanyProps;

/**
 * @description Job Detail. Renders job detail.
 * - **job** - ApiJobDetailOk | null - The job object.
 * - **locale** - Locale - The current app locale.
 * - **similarJobs** - ApiSchema<'JobResult'> - Similar jobs list.
 * - **apiPrevNextJobs** - ApiPrevNextJobOk - Prev & next jobs, for in page navigation (not modal). There are populated via an API request.
 * - **modalPrevNextJobs** - ModalPrevNextJobs - Prev & next jobs, for modal navigation. The data comes from SWR.
 * - **closeModal** - function - Function to close the modal, if opened in a modal, optional.
 * - **isLoading** - boolean - A flag indicating whether the job detail request is loading or not, optional.
 * - **isSinglePage** - boolean - A flag indicating if the page is single
 * - **isSkeletonPlaceholder** - boolean - A flag indicating whether this component is used as a skeleton placeholder for prev / next navigation in modal.
 * - **jobTitle** - string - The job title. Use this prop to display the job title regardless is the job details is loading or not.
 * - **modalNavigateToJob** - function - The function to use to navigate to prev / next job (in modal).
 * - **mode** - 'default' | 'preview' | 'company' - The mode of the component, used to control different rendering logic.
 */
export const JobDetail = (props: JobDetailProps) => {
  const {
    job,
    onTriggerRefund,
    locale,
    mutateUserDetail,
    similarJobs,
    apiPrevNextJobs,
    modalPrevNextJobs,
    closeModal,
    isLoading,
    isSkeletonPlaceholder,
    jobTitle,
    modalNavigateToJob,
    hideNavigation,
    mode,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  const searchParams = useSearchParams();
  const rid = searchParams?.get('rid') || '';

  const headerInfoData = useAppSelector(getUserHeaderInfo);
  const userId = headerInfoData?.userId;

  // Track the job detail view event (not company mode)
  // *****************************************
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mode !== 'company' && job?.slug) {
        trackEvent({
          eventType: 'view',
          entityType: 'job',
          entityId: job.slug,
          viewContext: 'detail',
          ...(userId && { userId: userId.toString() }),
          ...(rid && { listUuid: rid }),
        });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [job, mode, rid, userId]);

  // Helpers
  // *****************************************
  const isMc = !!useTwoColumnLayout();

  const isModal = useMemo(
    () => !!closeModal,
    [closeModal],
  );

  const showPlaceholders = useMemo(
    () => isLoading || isSkeletonPlaceholder,
    [isLoading, isSkeletonPlaceholder],
  );

  // Memoized value to determine if translations are available for the job.
  const hasTranslations = useMemo(
    () => false,
    [],
  );

  // State to determine whether to show translations or original content.
  // By default we will always show the original content, the user needs to click a button to see translations.
  const [showTranslations, setShowTranslations] = useState(false);


  // Function to retrieve the translated content for a given field.
  // the translation is removed for now, so we just return the original content.
  const getTranslatedField = useCallback((fieldName: string, original = ''): string => original, []);

  // Displayed job title
  const displayedJobTitle = useMemo(
    () => (jobTitle || getTranslatedField('title', job?.title)),
    [jobTitle, job?.title, getTranslatedField],
  );

  const displayedJobDescription = getTranslatedField('description', job?.description);

  // Toggle the `showTranslations` state.
  const toggleShowTranslations = () => {
    setShowTranslations(!showTranslations);
  };

  // Render component
  // ************************************
  return (
    <div className={clsx('relative w-full', {
      'lg:before:block lg:before:absolute lg:before:top-0 lg:before:bottom-0 lg:before:left-0 lg:before:right-1/2 lg:before:bg-primary/10': isModal,
    })}
    >
      <div className={clsx('relative flex flex-col bg-surface', {
        'mx-auto max-w-[1410px]': isModal,
        'sm:rounded-md': !isModal,
      })}
      >

        {/* Share and navigation - top bar */}
        {mode !== 'preview' && (
          <ShareAndNavigation
            job={job}
            apiPrevNextJobs={apiPrevNextJobs}
            modalPrevNextJobs={modalPrevNextJobs}
            closeJobModal={!isSkeletonPlaceholder ? closeModal : undefined}
            modalNavigateToJob={modalNavigateToJob}
            hideNavigation={hideNavigation}
            className="w-full px-4 py-2"
          />
        )}

        {/* Job Highlighted Border */}
        {job?.highlighted && !isModal && !isMc && (
          <span className="absolute -top-0.5 z-1 block h-1.5 w-full bg-secondary/50 sm:rounded-t-md" />
        )}

        <div className="lg:divide-surface-alt lg:flex lg:divide-x">

          {/* Job Card Sidebar */}
          <div className={clsx(
            'relative flex flex-col bg-surface p-4 lg:w-1/2 lg:p-8',
            !isMc && 'lg:min-h-screen',
            {
              'sm:rounded-t-md lg:rounded-tr-none lg:rounded-bl-md': !isModal,
            },
          )}
          >

            <div className={clsx('grow', isModal && 'lg:top-8', !isModal && 'lg:top-[--bj-mobile-menu-offset] lg:-mt-7 lg:pt-7')}>

              {/* Job Translations */}
              {hasTranslations && (
                <Button
                  styling="none"
                  className={clsx('mb-3 flex items-center gap-x-1.5 text-sm text-ink-medium hover:text-ink')}
                  onClick={toggleShowTranslations}
                >
                  <LanguageIcon className="size-4" />
                  {showTranslations ? t('global.label.see-original') : t('global.label.see-translation')}
                </Button>
              )}

              {/* Show activation info when mode is company */}
              {mode === 'company' && job && (
                <div className="mb-4">
                  <StatusBar
                    locale={locale}
                    job={{
                      slug: job.slug,
                      active: job.active,
                      numberOfViews: job.totalViews,
                      expiresIn: job.ownerDetail?.expiresIn ?? 0,
                      activationInfo: job.ownerDetail?.activationInfo,
                      ...(job.ownerDetail?.numberOfApplicants && { numberOfApplicants: job.ownerDetail.numberOfApplicants }),
                    }}
                  />

                  {job.ownerDetail?.refundable && onTriggerRefund
                    && (
                    <div className="mt-3">
                      <span className="block text-sm font-thin">
                        {t('job.refund.helper.text')}
                      </span>

                      <Button size="xs" styling="none" className="flex items-center text-sm underline" onClick={onTriggerRefund}>
                        <ReceiptRefundIcon className="size-5" />
                        {t('job.refund.button.text')}
                      </Button>
                    </div>
                    )}

                </div>
              )}

              {/* AI Evaluate Profile */}
              {job && (!job.workplaceJob && job?.applicationType as JobType !== JobType.GHOST_JOB_ID) && mode === 'default' && (
                <AiEvaluateProfile
                  mutateUserDetail={mutateUserDetail}
                  currentEvaluation={job?.evaluation}
                  jobSlug={job?.slug}
                  locale={locale}
                />
              )}

            </div>


          </div>

          {/* Job Card Content */}
          <div className={mode !== 'preview' ? 'p-4 lg:w-1/2 lg:p-8' : 'px-4 pb-4 lg:w-1/2 lg:px-8 lg:pb-8'}>

            {/* Job Title */}
            {displayedJobTitle && (
              <h2 className="mb-3 mt-1 break-words text-6xl font-semibold leading-tight tracking-tighter">
                {job ? (
                  <LinkStyled styling="none" href={`/job/${job.slug}`} className="hover:text-primary">
                    { displayedJobTitle }
                  </LinkStyled>
                ) : displayedJobTitle}
              </h2>
            )}

            {/* Archived / Applied badges below title */}
            {mode === 'default' && job && !job.active && (
              <div className="bg-surface-alt text-content-secondary inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-medium">
                <ArchiveBoxIcon className="size-5 shrink-0" />
                <span>{t('global.bnt.archived.job')}</span>
              </div>
            )}
            {mode === 'default' && job?.active && job?.applied && (
              <div className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-white">
                <InformationCircleIcon className="size-5 shrink-0" />
                <span>{t('job.applied')}</span>
              </div>
            )}

            {job && (
            <>
              {/* Inactive job */
                (!job.active) && mode !== 'company' && (
                  <Card className="mt-4 rounded border border-primary bg-surface px-4 py-2">
                    <SanitizeHTML
                      as="div"
                      htmlContent={similarJobs?.items?.length ? t('job.detail.inactive') : t('job.detail.inactive.noLink')}
                      className="prose"
                    />
                  </Card>
                )
              }

              {/* Job summary */}
              <Summary job={job} />

              {/* Company short */}
              {mode !== 'preview' && (
                <CompanyShort employer={job.employer} workplace={job.workplace} />
              )}
            </>
            )}

            {showPlaceholders && (
            <>
              {!displayedJobTitle && <TitlePlaceholder />}
              <SummaryPlaceholder />
              <CompanyShortPlaceholder />
            </>
            )}

            {/* Company Job Tags */}
            {mode === 'company' && job && (
            <>
              <ApplicantsTags job={job} />
              <CandidatesTags job={job} />
            </>
            )}

            {/* Tags */
            (!!job?.spokenLanguages?.length) && (
              <Tags spokenLanguages={job.spokenLanguages} />
            )
          }

            {/* Job description */
            displayedJobDescription && (
              <SanitizeHTML
                as="div"
                htmlContent={displayedJobDescription.replace(
                  /\s*<([a-z]+)(?:\s[^>]*)?>((?:\s|&nbsp;|<[^>]+>(?:\s|&nbsp;)*<\/[^>]+>)*)<\/\1>\s*/gi,
                  '',
                )}
                className={clsx(detailSectionsStyles.common, 'prose')}
                // Allow iframes in the description
                config={{
                  ADD_TAGS: ['iframe'],
                  ADD_ATTR: [
                    'allowfullscreen',
                    'frameborder',
                    'src',
                    'width',
                    'height',
                    'class',
                    'allow',
                  ],
                }}
              />
            )
          }

            {/* Online interview */
            !!job?.questions?.length && (
              <OnlineInterview questions={job.questions} />
            )
          }

            {/* Interview Stages */
            job?.interviewStages && (
              <div className={detailSectionsStyles.common}>
                <h3 className={detailSectionsStyles.title}>
                  { t('job.add.label.interview_stages') }
                </h3>
                <div className="mt-4">
                  {job.interviewStages}
                </div>
              </div>
            )
          }

            {/* Company Widget Box */ }
            {job && mode !== 'preview' && (
            <CompanyWidget employer={job.employer} workplace={job.workplace} />
            )}

            {/* Similar Jobs */
            !!similarJobs?.items?.length && job && (
              <SimilarJobs
                trackingData={{
                  listType: 'similar',
                }}
                similarJobs={similarJobs}
                jobSlug={job.slug}
                isModal={isModal}
                locale={locale}
              />
            )
          }

            {/* Navigation */
            apiPrevNextJobs && !hideNavigation && <Navigation apiPrevNextJobs={apiPrevNextJobs} />
          }


            {/* Job Content Loading Placeholders */}
            {showPlaceholders && (
            <div className="mr-2 mt-10 space-y-10 pt-10 md:pt-0">
              <TagsPlaceholder />
              <DescriptionPlaceholder />
              <CompanyWidgetPlaceholder />
            </div>
            )}
          </div>
        </div>
        {/* end lg:flex */}

        {/* Job Actions - placed after both columns so sticky bottom works on mobile */}
        {job && mode === 'default' && <JobActions job={job} mutateJob={mutateUserDetail} />}
      </div>
    </div>
  );
};
